// Premium radar / telemetry hero visual — CSS + SVG only (no deps).
// Represents Sentinel's live sensing field: concentric rings, a sweeping
// scan, sensor nodes, and a restrained red emergency blip.
export function HeroRadar() {
  const nodes = [
    { angle: -50, r: 116, label: "ACCEL", live: true },
    { angle: 28, r: 88, label: "GPS", live: true },
    { angle: 118, r: 132, label: "AUDIO", live: true },
    { angle: 196, r: 100, label: "GYRO", live: true },
    { angle: 268, r: 140, label: "MOTION", live: true },
  ];
  const cx = 170;
  const cy = 170;
  const pt = (angle, r) => {
    const a = (angle * Math.PI) / 180;
    return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
  };
  const [dangerX, dangerY] = pt(150, 152);

  return (
    <div className="relative mx-auto aspect-square w-full max-w-[460px]" data-testid="hero-radar">
      {/* ambient glow */}
      <div className="pointer-events-none absolute inset-0 spotlight-accent" />

      {/* sweeping scan */}
      <div className="absolute inset-[10%] rounded-full">
        <div
          className="absolute inset-0 rounded-full animate-radar-spin"
          style={{
            background:
              "conic-gradient(from 0deg, rgba(37,99,235,0.32), rgba(37,99,235,0.04) 60deg, transparent 120deg, transparent 360deg)",
            maskImage: "radial-gradient(circle, #000 62%, transparent 63%)",
            WebkitMaskImage: "radial-gradient(circle, #000 62%, transparent 63%)",
          }}
        />
      </div>

      <svg viewBox="0 0 340 340" className="relative h-full w-full">
        <defs>
          <radialGradient id="radar-core" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#2563EB" stopOpacity="0.5" />
            <stop offset="100%" stopColor="#2563EB" stopOpacity="0" />
          </radialGradient>
        </defs>

        {[46, 84, 122, 156].map((r) => (
          <circle key={r} cx={cx} cy={cy} r={r} fill="none" stroke="#1C1E26" strokeWidth="1" />
        ))}
        <line x1="14" y1={cy} x2="326" y2={cy} stroke="#14161d" strokeWidth="1" />
        <line x1={cx} y1="14" x2={cx} y2="326" stroke="#14161d" strokeWidth="1" />

        <circle cx={cx} cy={cy} r="120" fill="url(#radar-core)" />

        {nodes.map((n) => {
          const [x, y] = pt(n.angle, n.r);
          return (
            <g key={n.label}>
              <line x1={cx} y1={cy} x2={x} y2={y} stroke="#1C1E26" strokeWidth="1" />
              <circle cx={x} cy={y} r="7" fill="none" stroke="#2563EB" strokeOpacity="0.3" />
              <circle cx={x} cy={y} r="3.5" fill="#3B82F6" />
              <text
                x={x}
                y={y - 12}
                textAnchor="middle"
                className="fill-slate-500 font-mono"
                style={{ fontSize: 8, letterSpacing: 1 }}
              >
                {n.label}
              </text>
            </g>
          );
        })}

        {/* restrained red emergency blip */}
        <circle cx={dangerX} cy={dangerY} r="4.5" fill="#EF4444" />

        {/* center node */}
        <circle cx={cx} cy={cy} r="24" fill="#0A0B0F" stroke="#2563EB" strokeWidth="1.5" />
        <circle cx={cx} cy={cy} r="7" fill="#3B82F6" />
      </svg>

      {/* pulsing rings on the red blip */}
      <span
        className="absolute h-3 w-3 rounded-full bg-red-500/60 animate-ping-slow"
        style={{ left: `${(dangerX / 340) * 100}%`, top: `${(dangerY / 340) * 100}%` }}
      />

      {/* corner telemetry chips */}
      <div className="absolute left-0 top-6 hidden rounded-md border border-sentinel-border bg-sentinel-deep/80 px-3 py-2 backdrop-blur sm:block">
        <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-slate-500">Inputs</p>
        <p className="font-mono text-sm font-semibold text-slate-100">5 / 5 live</p>
      </div>
      <div className="absolute bottom-6 right-0 hidden rounded-md border border-red-500/30 bg-red-500/5 px-3 py-2 backdrop-blur sm:block">
        <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-red-400/80">Signal</p>
        <p className="font-mono text-sm font-semibold text-red-400">Anomaly</p>
      </div>
    </div>
  );
}
